<template>
  <div
    class="ma-0 mx-auto"
    style="min-height: 100%; max-width: 1000px;"
  >
    <v-card
      style="max-width: 700px;"
      class="mx-auto py-2"
    >
      <h2
        class="mb-5"
      >My Payments</h2>

      <v-autocomplete
        v-model="property_id"
        :items="propertyItems"
        label="Property"
        class="px-5 mb-3"
        hide-details
        dense
        outlined
      />

      <div
        class="d-flex align-center justify-space-around flex-wrap"
        style="width: 100%;"
      >
        <div
          class="d-flex align-center mb-3"
          style="width: 350px;"
        >
          <span
            class="pl-5 pr-3"
            style="width: 110px; text-align: left;"
          >Start Date:</span>

          <v-text-field
            v-model="startDate"
            type="date"
            class="pr-5"
            style="width: 200px"
            dense
            outlined
            hide-details
            :rules="[
              v => !!v || 'Start date is required',
              v => new Date(v).getTime() <= new Date(this.endDate).getTime() || 'Start date must be before end date',
            ]"
          />
        </div>

        <div
          class="d-flex align-center mb-3"
          style="width: 350px;"
        >
          <span
            class="pl-5 pr-3"
            style="width: 110px; text-align: left"
          >End Date:</span>

          <v-text-field
            v-model="endDate"
            type="date"
            class="pr-5"
            style="width: 200px"
            dense
            outlined
            hide-details
            :rules="[
              v => !!v || 'End date is required',
              v => new Date(v).getTime() >= new Date(this.startDate).getTime() || 'End date must be after start date',
            ]"
          />
        </div>
      </div>

      <v-expansion-panels
        v-if="propertyPayments && propertyPayments.length > 0"
        :key="propertyPayments.length"
        class="mx-auto px-0"
        popout
        flat
        tile
        hover
      >
        <v-expansion-panel
          v-for="(pay, i) in propertyPayments"
          v-if="pay.payment_date"
          :key="'payment' + i"
          class="mb-1"
        >
          <v-expansion-panel-header
            color="#E6E6E6"
          >
            <span><b>{{ capitalize(pay.type) }}&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;${{ pay.amount.toFixed(2) }}</b>&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;{{ convertDate(pay.payment_date) }}</span>
          </v-expansion-panel-header>

          <v-expansion-panel-content
            class="px-6 pt-3"
            color="white"
            style="color: black;"
          >
            For: <b>{{ capitalize(pay.type) }}</b><br>

            <div
              v-if="pay.type==='violation'"
            >Topic: <b>{{ pay.topic }}</b><br></div>

            <div
              v-if="pay.type==='due'"
            >Topic: <b>{{ pay.name }}</b><br></div>

            Payment Type: <b>{{ capitalize(pay.payment_type) }}</b><br>

            <div
              v-if="pay.payment_type==='PAYPAL'"
            >PayPal Confirmation ID: <b>{{ pay.paypal_confirm }}</b></div>

            Amount: <b>${{ pay.amount }}</b><br>

            Time: <b>{{ convertDateTime(pay.payment_date) }}</b><br>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div
        v-else-if="payments && payments.length > 0"
        class="d-flex flex-column align-center pa-3 mx-auto"
        style="width: 400px; max-width: 100%;"
      >
        <h4>You have no payments in this time range for the selected propert{{ this.property_id === 'all' ? 'ies' : 'y' }}.</h4>
      </div>

      <div
        v-else-if="!loading"
        class="d-flex flex-column align-center pa-3 mx-auto"
        style="width: 400px; max-width: 100%;"
      >
        <h4>You have no payment history.</h4>

        <v-btn
          color="accent"
          class="mx-auto mt-3"
          style="min-width: 150px;"
          @click="$router.back()"
        >
          <v-icon
            class="mr-1"
          >mdi-arrow-left</v-icon>

          <span>Back</span>
        </v-btn>
      </div>
    </v-card>

  </div>
</template>

<script>
export default {
  name: 'Payments',

  components: {

  },

  props: {
    permissions: {
      type: Number,
      required: true,
    },

    properties: {
      type: Array,
      required: true,
    },

    payments: {
      type: Array,
      required: true,
    },

    propList: {
      type: Array,
      required: true,
      default: [],
    },

    loading: {
      type: Boolean,
      required: true,
    },
  },

  data: () => ({
    propertyPayments: [],
    property_id: null,
    propertyItems: [],
    startDate: '',
    endDate: '',
  }),

  watch: {
    propList() {
      this.populatePropertyItems();
    },

    payments() {
      this.filterPayments();
    },

    property_id() {
      if (this.$route.query.property != this.property_id) {
        this.$router.replace({ query: { property: this.property_id, start: this.startDate, end: this.endDate } });
      }
      this.filterPayments();
    },

    startDate() {
      if (this.$route.query.start !== this.startDate) {
        this.$router.replace({ query: { property: this.property_id, start: this.startDate, end: this.endDate } });
      }
      this.filterPayments();
    },

    endDate() {
      if (this.$route.query.end !== this.endDate) {
        this.$router.replace({ query: { property: this.property_id, start: this.startDate, end: this.endDate } });
      }
      this.filterPayments();
    },
  },

  methods: {
    populatePropertyItems() {
      this.propertyItems = [{
        text: 'All',
        value: 'all',
      }];

      for (const p of this.propList) {
        this.propertyItems.push(p);
      }
    },

    UTCtoLocal(ts) {
      const d = new Date(ts);
      const offset = d.getTimezoneOffset();
      d.setMinutes(d.getMinutes() - offset);
      return d;
    },

    filterPayments() {
      if (this.property_id === 'all') {
        this.propertyPayments = this.payments.filter(f => this.UTCtoLocal(f.payment_date).getTime() >= new Date(this.startDate).getTime() && this.UTCtoLocal(f.payment_date).getTime() <= new Date(this.endDate).getTime());
      }
      else {
        this.propertyPayments = this.payments.filter(f => f.property_id == this.property_id && this.UTCtoLocal(f.payment_date).getTime() >= new Date(this.startDate).getTime() && this.UTCtoLocal(f.payment_date).getTime() <= new Date(this.endDate).getTime());
      }
    },

    capitalize(s) {
      return s.substr(0, 1).toUpperCase() + s.substr(1, s.length - 1);
    },

    convertDate(d) {
      if (d) {
        const d2 = new Date();
        d2.setUTCFullYear(parseInt(d.slice(0, 4)), parseInt(d.slice(5, 7)) - 1, parseInt(d.slice(8, 10)));
        d2.setUTCHours(0, 0, 0);
        return d2.toDateString().slice(4);
      }
    },

    convertDateTime(d) {
      if (d) {
        const d2 = new Date();
        d2.setUTCFullYear(parseInt(d.slice(0, 4)), parseInt(d.slice(5, 7)) - 1, parseInt(d.slice(8, 10)));
        d2.setUTCHours(parseInt(d.slice(12, 14)), parseInt(d.slice(15, 17)), parseInt(d.slice(18, 20)));
        return d2.toString().slice(4, 24)
      }
    },
  },

  created () {
    this.populatePropertyItems();

    if (parseInt(this.$route.query.property)) {
      this.property_id = parseInt(this.$route.query.property);
    }
    else {
      this.property_id = 'all';
    }

    if (this.$route.query.start) {
      this.startDate = this.$route.query.start;
    }
    else {
      let d = new Date();
      d.setMonth(d.getMonth() - 1);
      this.startDate = d.toISOString().slice(0, 10);
    }

    if (this.$route.query.end) {
      this.endDate = this.$route.query.end;
    }
    else {
      let d = new Date();
      this.endDate = d.toISOString().slice(0, 10);
    }

    this.filterPayments();
  },
};
</script>

<style>

</style>
